import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Box, Stack, Alert } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  PROMO_ALL_BRANDS_ID,
  PROMO_DEFAULT_MANUFACTURER,
  EMPTY_WEAK_SKU_RANKINGS,
  promoSimulatorService,
  resolvePerformanceClassParam,
  mapHistoricalSummaryToKpi,
  mapHistoricalTrendsToWeeklyPoints,
  mapSkuHealthApiToRows,
  mapWeakSkusApiToRankings,
  mapPromoDependencyApiToRows,
} from "./services/promoSimulatorService";
import {
  setBatchSimulationQueue,
  setBatchOptimizerQueue,
} from "../../Redux/promoSimulatorSlice/promoSimulatorSlice";
import {
  PERFORMANCE_SECTION_IDS,
  createSectionFilters,
  buildPerformanceFilterKey,
  getSectionWeekRange,
} from "./components/performance/helpers";
import { PerformanceFilterBar } from "./components/performance/PerformanceFilterBar";
import { HistoricalKPICards } from "./components/performance/HistoricalKPICards";
import { TrendsSection } from "./components/performance/TrendsSection";
import { SKUHealthDiagnosticsTable } from "./components/performance/SKUHealthDiagnosticsTable";
import { WeakSKUDetectionSection } from "./components/performance/WeakSKUDetectionSection";
import { PromoDependencyAnalysisPanel } from "./components/performance/PromoDependencyAnalysisPanel";
import { SelectedSkusActionBar } from "./components/performance/SelectedSkusActionBar";

export default function PerformanceHistory({ filters: initialFiltersProp, onSwitchTab }) {
  const dispatch = useDispatch();
  const scenarios = useSelector((s) => s.promoSimulator.scenarios);
  const projectId = useSelector((s) => s.userProject?.projectId);

  // ── Shared SKU selection (Weak SKU + Promo Dependency panels) ─────────────

  const [selectedSkus, setSelectedSkus] = useState(new Set());

  const toggleSku = useCallback((sku, forceState) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      const shouldAdd = forceState !== undefined ? forceState : !next.has(sku);
      if (shouldAdd) next.add(sku);
      else next.delete(sku);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedSkus(new Set()), []);

  // ── API state ─────────────────────────────────────────────────────────────

  const [historicalKpi, setHistoricalKpi] = useState(null);
  const [historicalKpiLoading, setHistoricalKpiLoading] = useState(false);
  const [historicalKpiError, setHistoricalKpiError] = useState(null);

  const [historicalWeeklyPoints, setHistoricalWeeklyPoints] = useState([]);
  const [historicalTrendsLoading, setHistoricalTrendsLoading] = useState(false);
  const [historicalTrendsError, setHistoricalTrendsError] = useState(null);
  const [historicalTrendsLoadedKey, setHistoricalTrendsLoadedKey] = useState(null);

  const [apiSkuHealth, setApiSkuHealth] = useState([]);
  const [skuHealthLoading, setSkuHealthLoading] = useState(false);
  const [skuHealthError, setSkuHealthError] = useState(null);
  const [skuHealthLoadedKey, setSkuHealthLoadedKey] = useState(null);

  const [weakSkusRankings, setWeakSkusRankings] = useState(EMPTY_WEAK_SKU_RANKINGS);
  const [weakSkusLoading, setWeakSkusLoading] = useState(false);
  const [weakSkusError, setWeakSkusError] = useState(null);
  const [weakSkusLoadedKey, setWeakSkusLoadedKey] = useState(null);

  const [apiPromoDependency, setApiPromoDependency] = useState([]);
  const [promoDepLoading, setPromoDepLoading] = useState(false);
  const [promoDepError, setPromoDepError] = useState(null);
  const [promoDepLoadedKey, setPromoDepLoadedKey] = useState(null);

  const historicalTrendsRequestIdRef = useRef(0);
  const skuHealthRequestIdRef = useRef(0);
  const weakSkusRequestIdRef = useRef(0);
  const promoDepRequestIdRef = useRef(0);

  // ── Per-section filters ───────────────────────────────────────────────────

  const [sectionFilters, setSectionFilters] = useState(() => {
    const base = createSectionFilters({
      manufacturer: initialFiltersProp?.manufacturer,
      brand: initialFiltersProp?.brand,
      class: initialFiltersProp?.class ?? null,
      startWeek: initialFiltersProp?.startWeek,
      endWeek: initialFiltersProp?.endWeek,
      year: initialFiltersProp?.year,
    });
    return PERFORMANCE_SECTION_IDS.reduce((acc, id) => {
      acc[id] = { ...base };
      return acc;
    }, {});
  });

  const updateSectionFilters = useCallback((sectionId, updater) => {
    setSectionFilters((prev) => ({
      ...prev,
      [sectionId]:
        typeof updater === "function" ? updater(prev[sectionId]) : updater,
    }));
  }, []);

  const kpiFilters = sectionFilters.kpi;
  const trendsFilters = sectionFilters.trends;
  const skuHealthFilters = sectionFilters.skuHealth;
  const weakSkusFilters = sectionFilters.weakSkus;
  const promoDepFilters = sectionFilters.promoDep;

  const kpiFilterKey = useMemo(
    () => buildPerformanceFilterKey(projectId, kpiFilters),
    [projectId, kpiFilters]
  );
  const trendsFilterKey = useMemo(
    () => buildPerformanceFilterKey(projectId, trendsFilters),
    [projectId, trendsFilters]
  );
  const skuHealthFilterKey = useMemo(
    () => buildPerformanceFilterKey(projectId, skuHealthFilters),
    [projectId, skuHealthFilters]
  );
  const weakSkusFilterKey = useMemo(
    () => buildPerformanceFilterKey(projectId, weakSkusFilters),
    [projectId, weakSkusFilters]
  );
  const promoDepFilterKey = useMemo(
    () => buildPerformanceFilterKey(projectId, promoDepFilters),
    [projectId, promoDepFilters]
  );

  const kpiWeekRange = useMemo(() => getSectionWeekRange(kpiFilters), [kpiFilters]);
  const trendsWeekRange = useMemo(() => getSectionWeekRange(trendsFilters), [trendsFilters]);
  const skuHealthWeekRange = useMemo(() => getSectionWeekRange(skuHealthFilters), [skuHealthFilters]);
  const weakSkusWeekRange = useMemo(() => getSectionWeekRange(weakSkusFilters), [weakSkusFilters]);
  const promoDepWeekRange = useMemo(() => getSectionWeekRange(promoDepFilters), [promoDepFilters]);

  const skuHealthBrandFallback =
    skuHealthFilters.brand && skuHealthFilters.brand !== PROMO_ALL_BRANDS_ID
      ? skuHealthFilters.brand
      : null;

  const weakSkusBrandFallback =
    weakSkusFilters.brand && weakSkusFilters.brand !== PROMO_ALL_BRANDS_ID
      ? weakSkusFilters.brand
      : null;

  // ── Display state (stale-while-loading pattern) ───────────────────────────

  const historicalTrendsDisplayPoints =
    historicalTrendsLoadedKey === trendsFilterKey ? historicalWeeklyPoints : [];
  const historicalTrendsDisplayLoading =
    !projectId || historicalTrendsLoading || historicalTrendsLoadedKey !== trendsFilterKey;

  const skuHealthDisplayRows =
    skuHealthLoadedKey === skuHealthFilterKey ? apiSkuHealth : [];
  const skuHealthDisplayLoading =
    !projectId || skuHealthLoading || skuHealthLoadedKey !== skuHealthFilterKey;

  const weakSkusDisplayRankings =
    weakSkusLoadedKey === weakSkusFilterKey ? weakSkusRankings : EMPTY_WEAK_SKU_RANKINGS;
  const weakSkusDisplayLoading =
    !projectId || weakSkusLoading || weakSkusLoadedKey !== weakSkusFilterKey;

  const promoDepDisplayRows =
    promoDepLoadedKey === promoDepFilterKey ? apiPromoDependency : [];
  const promoDepDisplayLoading =
    !projectId || promoDepLoading || promoDepLoadedKey !== promoDepFilterKey;

  // ── SKU metadata map (for batch send) ────────────────────────────────────

  const skuBatchMetaMap = useMemo(() => {
    const map = {};

    const addRow = (row, filters) => {
      if (!row?.sku || row.sku === "—") return;
      const existing = map[row.sku] || {};
      map[row.sku] = {
        brand: row.brand && row.brand !== "—" ? row.brand : existing.brand,
        class:
          (row.class && String(row.class).trim()) || filters.class || existing.class,
        manufacturer: filters.manufacturer || existing.manufacturer,
      };
    };

    skuHealthDisplayRows.forEach((row) => addRow(row, skuHealthFilters));
    [
      ...weakSkusDisplayRankings.declining,
      ...weakSkusDisplayRankings.lowestRoi,
      ...weakSkusDisplayRankings.highestPromoDep,
      ...weakSkusDisplayRankings.highestCannibal,
    ].forEach((row) => addRow(row, weakSkusFilters));
    promoDepDisplayRows.forEach((row) => addRow(row, promoDepFilters));

    if (Array.isArray(scenarios)) {
      scenarios.forEach((s) => {
        const skus = Array.isArray(s.skus) ? s.skus : [s.skus].filter(Boolean);
        skus.forEach((sku) => {
          if (!sku) return;
          const existing = map[sku] || {};
          map[sku] = {
            brand: s.brand || existing.brand,
            class: s.class || existing.class,
            manufacturer: s.manufacturer || existing.manufacturer,
          };
        });
      });
    }

    return map;
  }, [
    scenarios,
    skuHealthDisplayRows,
    skuHealthFilters,
    weakSkusDisplayRankings,
    weakSkusFilters,
    promoDepDisplayRows,
    promoDepFilters,
  ]);

  // ── Batch send handlers ───────────────────────────────────────────────────

  const handleSendToSimulator = useCallback(() => {
    const items = [...selectedSkus].map((sku) => {
      const meta = skuBatchMetaMap[sku] || {};
      return { sku, brand: meta.brand || "—", class: meta.class || null, manufacturer: meta.manufacturer || null };
    });
    dispatch(setBatchSimulationQueue(items));
    clearSelection();
    if (onSwitchTab) onSwitchTab(1);
  }, [selectedSkus, skuBatchMetaMap, dispatch, clearSelection, onSwitchTab]);

  const handleSendToOptimizer = useCallback(() => {
    const items = [...selectedSkus].map((sku) => {
      const meta = skuBatchMetaMap[sku] || {};
      return { sku, brand: meta.brand || "—" };
    });
    dispatch(setBatchOptimizerQueue(items));
    clearSelection();
    if (onSwitchTab) onSwitchTab(3);
  }, [selectedSkus, skuBatchMetaMap, dispatch, clearSelection, onSwitchTab]);

  // ── API effects ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!projectId) {
      setHistoricalKpi(null);
      setHistoricalKpiError("Select a project to load historical KPIs.");
      setHistoricalKpiLoading(false);
      return undefined;
    }

    const productClass = resolvePerformanceClassParam(kpiFilters.class);
    let cancelled = false;
    setHistoricalKpiLoading(true);
    setHistoricalKpiError(null);

    promoSimulatorService
      .getHistoricalSummary({
        projectId,
        manufacturer: kpiFilters.manufacturer ?? PROMO_DEFAULT_MANUFACTURER,
        brand: kpiFilters.brand,
        productClass: productClass ?? undefined,
        year: kpiWeekRange.year,
        startWeek: kpiWeekRange.weekStart,
        endWeek: kpiWeekRange.weekEnd,
      })
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.summary) {
          setHistoricalKpi(mapHistoricalSummaryToKpi(res.summary));
          setHistoricalKpiError(null);
        } else {
          setHistoricalKpi(null);
          setHistoricalKpiError(res.error || "Unable to load historical summary.");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setHistoricalKpi(null);
        setHistoricalKpiError("Unable to load historical summary.");
      })
      .finally(() => {
        if (!cancelled) setHistoricalKpiLoading(false);
      });

    return () => { cancelled = true; };
  }, [projectId, kpiFilterKey, kpiFilters, kpiWeekRange]);

  useEffect(() => {
    if (!projectId) {
      setHistoricalWeeklyPoints([]);
      setHistoricalTrendsLoadedKey(null);
      setHistoricalTrendsError("Select a project to load weekly trends.");
      setHistoricalTrendsLoading(false);
      return undefined;
    }

    const productClass = resolvePerformanceClassParam(trendsFilters.class);
    const requestId = ++historicalTrendsRequestIdRef.current;
    const filterKeyAtRequest = trendsFilterKey;
    setHistoricalTrendsLoading(true);

    promoSimulatorService
      .getHistoricalTrends({
        projectId,
        manufacturer: trendsFilters.manufacturer ?? PROMO_DEFAULT_MANUFACTURER,
        brand: trendsFilters.brand,
        productClass: productClass ?? undefined,
        year: trendsWeekRange.year,
        startWeek: trendsWeekRange.weekStart,
        endWeek: trendsWeekRange.weekEnd,
        granularity: "week",
      })
      .then((res) => {
        if (requestId !== historicalTrendsRequestIdRef.current) return;
        if (res.success) {
          setHistoricalWeeklyPoints(mapHistoricalTrendsToWeeklyPoints(res.trends));
          setHistoricalTrendsLoadedKey(filterKeyAtRequest);
          setHistoricalTrendsError(null);
        } else {
          setHistoricalWeeklyPoints([]);
          setHistoricalTrendsLoadedKey(filterKeyAtRequest);
          setHistoricalTrendsError(res.error || "Unable to load historical trends.");
        }
      })
      .catch(() => {
        if (requestId !== historicalTrendsRequestIdRef.current) return;
        setHistoricalWeeklyPoints([]);
        setHistoricalTrendsLoadedKey(filterKeyAtRequest);
        setHistoricalTrendsError("Unable to load historical trends.");
      })
      .finally(() => {
        if (requestId !== historicalTrendsRequestIdRef.current) return;
        setHistoricalTrendsLoading(false);
      });

    return undefined;
  }, [projectId, trendsFilterKey, trendsFilters, trendsWeekRange]);

  useEffect(() => {
    if (!projectId) {
      setApiSkuHealth([]);
      setSkuHealthLoadedKey(null);
      setSkuHealthError("Select a project to load SKU health.");
      setSkuHealthLoading(false);
      return undefined;
    }

    const productClass = resolvePerformanceClassParam(skuHealthFilters.class);
    const requestId = ++skuHealthRequestIdRef.current;
    const filterKeyAtRequest = skuHealthFilterKey;
    setSkuHealthLoading(true);

    promoSimulatorService
      .getSkuHealth({
        projectId,
        manufacturer: skuHealthFilters.manufacturer ?? PROMO_DEFAULT_MANUFACTURER,
        brand: skuHealthFilters.brand,
        productClass: productClass ?? undefined,
        year: skuHealthWeekRange.year,
        startWeek: skuHealthWeekRange.weekStart,
        endWeek: skuHealthWeekRange.weekEnd,
      })
      .then((res) => {
        if (requestId !== skuHealthRequestIdRef.current) return;
        if (res.success) {
          setApiSkuHealth(mapSkuHealthApiToRows(res.skuHealth, skuHealthBrandFallback));
          setSkuHealthLoadedKey(filterKeyAtRequest);
          setSkuHealthError(null);
        } else {
          setApiSkuHealth([]);
          setSkuHealthLoadedKey(filterKeyAtRequest);
          setSkuHealthError(res.error || "Unable to load SKU health.");
        }
      })
      .catch(() => {
        if (requestId !== skuHealthRequestIdRef.current) return;
        setApiSkuHealth([]);
        setSkuHealthLoadedKey(filterKeyAtRequest);
        setSkuHealthError("Unable to load SKU health.");
      })
      .finally(() => {
        if (requestId !== skuHealthRequestIdRef.current) return;
        setSkuHealthLoading(false);
      });

    return undefined;
  }, [projectId, skuHealthFilterKey, skuHealthFilters, skuHealthWeekRange, skuHealthBrandFallback]);

  useEffect(() => {
    if (!projectId) {
      setWeakSkusRankings(EMPTY_WEAK_SKU_RANKINGS);
      setWeakSkusLoadedKey(null);
      setWeakSkusError("Select a project to load weak SKU rankings.");
      setWeakSkusLoading(false);
      return undefined;
    }

    const productClass = resolvePerformanceClassParam(weakSkusFilters.class);
    const requestId = ++weakSkusRequestIdRef.current;
    const filterKeyAtRequest = weakSkusFilterKey;
    setWeakSkusLoading(true);

    promoSimulatorService
      .getWeakSkus({
        projectId,
        manufacturer: weakSkusFilters.manufacturer ?? PROMO_DEFAULT_MANUFACTURER,
        brand: weakSkusFilters.brand,
        productClass: productClass ?? undefined,
        year: weakSkusWeekRange.year,
        startWeek: weakSkusWeekRange.weekStart,
        endWeek: weakSkusWeekRange.weekEnd,
      })
      .then((res) => {
        if (requestId !== weakSkusRequestIdRef.current) return;
        if (res.success) {
          setWeakSkusRankings(
            mapWeakSkusApiToRankings(res.weakSkus, weakSkusBrandFallback)
          );
          setWeakSkusLoadedKey(filterKeyAtRequest);
          setWeakSkusError(null);
        } else {
          setWeakSkusRankings(EMPTY_WEAK_SKU_RANKINGS);
          setWeakSkusLoadedKey(filterKeyAtRequest);
          setWeakSkusError(res.error || "Unable to load weak SKU rankings.");
        }
      })
      .catch(() => {
        if (requestId !== weakSkusRequestIdRef.current) return;
        setWeakSkusRankings(EMPTY_WEAK_SKU_RANKINGS);
        setWeakSkusLoadedKey(filterKeyAtRequest);
        setWeakSkusError("Unable to load weak SKU rankings.");
      })
      .finally(() => {
        if (requestId !== weakSkusRequestIdRef.current) return;
        setWeakSkusLoading(false);
      });

    return undefined;
  }, [projectId, weakSkusFilterKey, weakSkusFilters, weakSkusWeekRange, weakSkusBrandFallback]);

  useEffect(() => {
    if (!projectId) {
      setApiPromoDependency([]);
      setPromoDepLoadedKey(null);
      setPromoDepError("Select a project to load promo dependency analysis.");
      setPromoDepLoading(false);
      return undefined;
    }

    const productClass = resolvePerformanceClassParam(promoDepFilters.class);
    const requestId = ++promoDepRequestIdRef.current;
    const filterKeyAtRequest = promoDepFilterKey;
    setPromoDepLoading(true);

    promoSimulatorService
      .getPromoDependency({
        projectId,
        manufacturer: promoDepFilters.manufacturer ?? PROMO_DEFAULT_MANUFACTURER,
        brand: promoDepFilters.brand,
        productClass: productClass ?? undefined,
        year: promoDepWeekRange.year,
        startWeek: promoDepWeekRange.weekStart,
        endWeek: promoDepWeekRange.weekEnd,
      })
      .then((res) => {
        if (requestId !== promoDepRequestIdRef.current) return;
        if (res.success) {
          setApiPromoDependency(mapPromoDependencyApiToRows(res.promoDependency));
          setPromoDepLoadedKey(filterKeyAtRequest);
          setPromoDepError(null);
        } else {
          setApiPromoDependency([]);
          setPromoDepLoadedKey(filterKeyAtRequest);
          setPromoDepError(res.error || "Unable to load promo dependency analysis.");
        }
      })
      .catch(() => {
        if (requestId !== promoDepRequestIdRef.current) return;
        setApiPromoDependency([]);
        setPromoDepLoadedKey(filterKeyAtRequest);
        
        setPromoDepError("Unable to load promo dependency analysis.");
      })
      .finally(() => {
        if (requestId !== promoDepRequestIdRef.current) return;
        setPromoDepLoading(false);
      });

    return undefined;
  }, [projectId, promoDepFilterKey, promoDepFilters, promoDepWeekRange]);

  // ── Filter bars (memoised to avoid re-creating on every render) ───────────

  const sectionFilterBars = useMemo(
    () =>
      PERFORMANCE_SECTION_IDS.reduce((acc, sectionId) => {
        acc[sectionId] = (
          <PerformanceFilterBar
            key={`${sectionId}-filters`}
            filters={sectionFilters[sectionId]}
            setFilters={(updater) => updateSectionFilters(sectionId, updater)}
            sx={{ mb: 0 }}
          />
        );
        return acc;
      }, {}),
    [sectionFilters, updateSectionFilters]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <HistoricalKPICards
        kpi={historicalKpi}
        loading={historicalKpiLoading}
        error={historicalKpiError}
        filterBar={sectionFilterBars.kpi}
      />

      <Box sx={{ mt: 3 }}>
        <TrendsSection
          key={trendsFilterKey}
          weeklyPoints={historicalTrendsDisplayPoints}
          loading={historicalTrendsDisplayLoading}
          error={historicalTrendsError}
          globalWeekStart={trendsWeekRange.weekStart}
          globalWeekEnd={trendsWeekRange.weekEnd}
          filterBar={sectionFilterBars.trends}
        />
      </Box>

      <Stack spacing={3} sx={{ mt: 3 }}>
        {skuHealthError && <Alert severity="warning">{skuHealthError}</Alert>}
        <SKUHealthDiagnosticsTable
          skuHealth={skuHealthDisplayRows}
          loading={skuHealthDisplayLoading}
          filterBar={sectionFilterBars.skuHealth}
        />
        <WeakSKUDetectionSection
          rankings={weakSkusDisplayRankings}
          loading={weakSkusDisplayLoading}
          error={weakSkusError}
          selectedSkus={selectedSkus}
          onToggleSku={toggleSku}
          filterBar={sectionFilterBars.weakSkus}
        />
        <PromoDependencyAnalysisPanel
          skuPromoDependency={promoDepDisplayRows}
          loading={promoDepDisplayLoading}
          error={promoDepError}
          selectedSkus={selectedSkus}
          onToggleSku={toggleSku}
          filterBar={sectionFilterBars.promoDep}
        />
      </Stack>

      <SelectedSkusActionBar
        selectedSkus={selectedSkus}
        onClear={clearSelection}
        onSendToSimulator={handleSendToSimulator}
        onSendToOptimizer={handleSendToOptimizer}
      />
    </Box>
  );
}
